/**
 * Campaign Roster — Pure logic for team unit management.
 * No DOM, no Three.js.
 */
import type { CampaignUnit } from '../types';
import { SeededRandom } from './random';
import { MAX_CAMPAIGN_UNITS, RECRUIT_NAMES, BOT_PRESETS, SKILL_PROGRESSION, MAX_UPGRADE_LEVEL, MAX_AP_LEVEL } from '../constants';

// =============================================================================
// ROSTER CREATION
// =============================================================================

export function createDefaultRoster(): CampaignUnit[] {
  return [{
    id: 0,
    name: 'Commander',
    personalityKey: 'balanced',
    skillKey: 'medium',
    alive: true,
    ap: 1,
    maxAp: 1,
  }];
}

// =============================================================================
// RECRUITMENT
// =============================================================================

export function recruitUnit(roster: CampaignUnit[], seed: string): CampaignUnit[] {
  if (roster.length >= MAX_CAMPAIGN_UNITS) return roster;

  const rng = new SeededRandom(seed);

  // Pick unused name
  const usedNames = new Set(roster.map(u => u.name));
  const availableNames = RECRUIT_NAMES.filter(n => !usedNames.has(n));
  const name = availableNames.length > 0
    ? availableNames[rng.int(0, availableNames.length - 1)]
    : `Recruit ${roster.length}`;

  // Pick random personality
  const personalityKeys = Object.keys(BOT_PRESETS);
  const personalityKey = personalityKeys[rng.int(0, personalityKeys.length - 1)];

  const newUnit: CampaignUnit = {
    id: roster.length,
    name,
    personalityKey,
    skillKey: 'easy',
    alive: true,
    ap: 1,
    maxAp: 1,
  };

  return [...roster, newUnit];
}

// =============================================================================
// FILTERING
// =============================================================================

export function getAliveUnits(roster: CampaignUnit[]): CampaignUnit[] {
  return roster.filter(u => u.alive);
}

export function getAliveBotUnits(roster: CampaignUnit[]): CampaignUnit[] {
  return roster.filter(u => u.alive && u.id !== 0);
}

// =============================================================================
// DEATH / REVIVAL
// =============================================================================

export function markUnitsDead(
  roster: CampaignUnit[],
  deadIds: number[],
  permadeath: boolean
): CampaignUnit[] {
  if (!permadeath) return roster; // normal mode: no-op
  return roster.map(u => {
    if (u.id === 0) return u; // commander is immune
    if (deadIds.includes(u.id)) return { ...u, alive: false };
    return u;
  });
}

export function reviveAllUnits(roster: CampaignUnit[]): CampaignUnit[] {
  return roster.map(u => ({ ...u, alive: true, ap: u.maxAp }));
}

export function resetAllUnitAp(roster: CampaignUnit[]): CampaignUnit[] {
  return roster.map(u => ({ ...u, ap: u.maxAp }));
}

export function spendUnitAp(roster: CampaignUnit[], unitIds: number[]): CampaignUnit[] {
  return roster.map(u =>
    unitIds.includes(u.id) ? { ...u, ap: Math.max(0, u.ap - 1) } : u
  );
}

// =============================================================================
// EDITING
// =============================================================================

export function renameUnit(
  roster: CampaignUnit[],
  unitId: number,
  newName: string
): CampaignUnit[] {
  const trimmed = newName.trim().slice(0, 20);
  if (!trimmed) return roster;
  return roster.map(u =>
    u.id === unitId ? { ...u, name: trimmed } : u
  );
}

// =============================================================================
// UPGRADES
// =============================================================================

export function upgradeUnitAp(roster: CampaignUnit[], unitId: number): CampaignUnit[] {
  return roster.map(u => {
    if (u.id !== unitId) return u;
    if (u.maxAp >= MAX_AP_LEVEL) return u;
    return { ...u, maxAp: u.maxAp + 1, ap: u.ap + 1 };
  });
}

export function upgradeUnitSkill(roster: CampaignUnit[], unitId: number): CampaignUnit[] {
  return roster.map(u => {
    if (u.id !== unitId) return u;
    const idx = SKILL_PROGRESSION.indexOf(u.skillKey);
    if (idx < 0 || idx >= SKILL_PROGRESSION.length - 1) return u;
    return { ...u, skillKey: SKILL_PROGRESSION[idx + 1] };
  });
}

export function upgradeUnitFitness(roster: CampaignUnit[], unitId: number): CampaignUnit[] {
  return roster.map(u => {
    if (u.id !== unitId) return u;
    const level = u.fitnessLevel ?? 0;
    if (level >= MAX_UPGRADE_LEVEL) return u;
    return { ...u, fitnessLevel: level + 1 };
  });
}

export function upgradeUnitJump(roster: CampaignUnit[], unitId: number): CampaignUnit[] {
  return roster.map(u => {
    if (u.id !== unitId) return u;
    const level = u.jumpLevel ?? 0;
    if (level >= MAX_UPGRADE_LEVEL) return u;
    return { ...u, jumpLevel: level + 1 };
  });
}
